/** @format */

import {Box, Button, Typography} from '@mui/material';
import {alpha, styled} from '@mui/material/styles';

/**
 * Outer wrapper for the "no mediaplan" placeholder.
 *
 * Both viewModes share the same diagonal cross-hatch background (45° + 135°
 * diamond lattice) from Figma nodes 10284:125368 and 10286:128514.
 *
 * Layout:
 *  - dynamic: flex column, flex: 1 0 0 (grows to fill left half of metrics row)
 *  - summary: spans full KPI grid width; caller controls sizing via sx/className
 *
 * Hatch: two CSS repeating-linear-gradients at 45° and 135° produce the
 * diamond lattice seen in both screenshots. Stripe color = bad/bad (#C06868) at 20%
 * over surface-1 background, 1px stripes, 12px spacing.
 */
export const CtaRoot = styled(Box, {
  shouldForwardProp: prop => prop !== 'fullWidth',
})<{fullWidth?: boolean}>(({theme, fullWidth}) => {
  // Grid line color: bad/bad at 20% opacity (#C06868 @ 0.2).
  const gridColor = alpha(theme.palette.semantic_dark?.bad.main ?? '#C06868', 0.2);

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    // dynamic: grow to fill the KPI column's full height (= the taller Расход column) so the hatch
    // reaches the bottom of the block. summary: content height inside the narrow box.
    flexGrow: fullWidth ? 0 : 1,
    padding: `${theme.spacing(8)} ${theme.spacing(4)}`,

    // Cross-hatch (diamond) background: two diagonal stripe sets create the lattice.
    // 1px stripes, 12px pitch, on the surface-1 base.
    backgroundImage: `
      repeating-linear-gradient(
        45deg,
        ${gridColor} 0px,
        ${gridColor} 1px,
        transparent 1px,
        transparent 12px
      ),
      repeating-linear-gradient(
        135deg,
        ${gridColor} 0px,
        ${gridColor} 1px,
        transparent 1px,
        transparent 12px
      )
    `,
    backgroundColor: theme.palette.semantic_dark?.surface.surface1,
  };
});

export const CtaInner = styled(Box, {
  shouldForwardProp: prop => prop !== 'fullWidth',
})<{fullWidth?: boolean}>(({theme, fullWidth}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: fullWidth ? 'center' : 'flex-start',
  gap: theme.spacing(2.5), // 10px
  ...(fullWidth && {width: '100%'}),
}));

// "В проекте не настроен медиаплан" — 14px / 16px regular, white (#ffffff)
export const CtaLabel = styled(Typography)(({theme}) => ({
  fontSize: 14,
  lineHeight: '16px',
  fontWeight: 400,
  color: theme.palette.semantic_dark?.surface.onSurface,
  whiteSpace: 'nowrap',
}));

// "Настроить медиаплан" button — solid primary, 8px radius, px-16 py-8, full width of inner block.
// Figma tokens: bg = primary/primary (#3e88c5), text = primary/on-primary (#f9fcfe)
export const SetupButton = styled(Button)(({theme}) => ({
  backgroundColor: theme.palette.semantic_dark?.primary.main,
  color: theme.palette.semantic_dark?.primary.on,
  borderRadius: 8,
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  fontSize: 14,
  lineHeight: '16px',
  fontWeight: 400,
  textTransform: 'none',
  width: '100%',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: theme.palette.semantic_dark?.primary.hover,
  },
}));
